/* eslint-disable @change-corgi/require-data-qa-attribute -- shared component requires consumers to provide data-qa */
import type React from 'react';
import type { ComponentProps } from 'react';
import { useCallback } from 'react';

import { Button, Spinner } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import { TypedTranslate } from '@change-corgi/core/react/i18n';

import { useFocusFallbackManualTrigger } from 'src/app/shared/hooks/focus';

import { ActionBar } from '../ActionBar';

const TYPES = { form: 'submit', navigation: 'button' } as const;

type ButtonOnClick = NonNullable<ComponentProps<typeof Button>['onClick']>;
type PressEvent = Parameters<ButtonOnClick>[0];

type SecondaryActionButtonProps = ComponentProps<typeof Button> & {
	label: TranslationKey;
	onClick: ButtonOnClick;
};

export const SecondaryActionButton: React.FC<SecondaryActionButtonProps> = ({ label, onClick, ...rest }) => {
	const triggerFocusFallback = useFocusFallbackManualTrigger();
	const handleClick = (e: PressEvent) => {
		onClick(e);
		triggerFocusFallback(e.target);
	};

	return (
		<Button type="button" variant="secondary" onClick={handleClick} data-qa="secondary-action" {...rest}>
			<TypedTranslate value={label} />
		</Button>
	);
};

type PrimaryActionButtonProps = ComponentProps<typeof Button> & {
	label: TranslationKey;
	loadingLabel?: TranslationKey;
	onClick: ButtonOnClick;
	type: 'submit' | 'button';
};

export const PrimaryActionButton: React.FC<PrimaryActionButtonProps> = ({
	label,
	onClick,
	type,
	className,
	loading,
	loadingLabel,
	...rest
}) => {
	const triggerFocusFallback = useFocusFallbackManualTrigger();
	const isLoading = !!loading;
	const resolvedLoadingLabel = loadingLabel || label;
	const handleClick = useCallback(
		(e: PressEvent) => {
			if (isLoading) return;

			onClick(e);
			triggerFocusFallback(e.target);
		},
		[isLoading, onClick, triggerFocusFallback],
	);

	return (
		<Button
			type={type}
			variant="primary"
			onClick={handleClick}
			className={className}
			data-qa="primary-action"
			aria-disabled={isLoading}
			{...rest}
		>
			<span className="relative inline-grid place-items-center">
				<span className={cn('col-start-1 row-start-1', { invisible: isLoading })} aria-hidden={isLoading}>
					<TypedTranslate value={label} />
				</span>
				{isLoading && <Spinner className="col-start-1 row-start-1 size-5" aria-hidden />}
			</span>
			<span className="sr-only" role="status" aria-live="polite" aria-atomic>
				{isLoading && <TypedTranslate value={resolvedLoadingLabel} />}
			</span>
		</Button>
	);
};

type OnClick = () => Promise<void> | void;
type BaseStepActionProps = {
	onPrimaryAction?: OnClick;
	onSecondaryAction?: OnClick;
	primaryActionLabel?: TranslationKey;
	primaryActionIcon?: ComponentProps<typeof Button>['leadingIcon'];
	secondaryActionLabel?: TranslationKey;
	submittingLabel?: TranslationKey;
	className?: string;
	submitting?: boolean;
};
type FormStepActionProps = { type: 'form' } & BaseStepActionProps;
type NavigationStepActionProps = { type: 'navigation' } & BaseStepActionProps;
type StepActionsProps = FormStepActionProps | NavigationStepActionProps;

export const StepActions: React.FC<StepActionsProps> = ({
	onSecondaryAction,
	onPrimaryAction,
	type,
	className,
	primaryActionLabel,
	primaryActionIcon,
	secondaryActionLabel,
	submitting = false,
	submittingLabel,
}) => {
	const showSecondaryActionButton = onSecondaryAction !== undefined;

	return (
		<ActionBar className={className}>
			<div className={cn('grid gap-4', showSecondaryActionButton ? 'grid-cols-[1fr_auto]' : 'grid-cols-1')}>
				{showSecondaryActionButton && (
					<div className="ml-auto">
						<SecondaryActionButton
							onClick={onSecondaryAction}
							label={secondaryActionLabel || translationKey('corgi.start_a_petition.step_actions.button.back')}
						/>
					</div>
				)}
				<div className="inline-flex">
					<PrimaryActionButton
						onClick={onPrimaryAction ?? (() => {})}
						label={primaryActionLabel || translationKey('corgi.start_a_petition.step_actions.button.forward')}
						leadingIcon={primaryActionIcon}
						type={TYPES[type]}
						className="mx-auto w-full md:max-w-[300px]"
						loading={submitting}
						loadingLabel={submittingLabel}
					/>
				</div>
			</div>
		</ActionBar>
	);
};
